import React from 'react';

function Pill({ label, value, kind, title }) {
  const cls = kind === 'ok'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : kind === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-red-200 bg-red-50 text-red-800';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}
      title={title || ''}
    >
      {label}: {value}
    </span>
  );
}

function toLineList(arr, max = 12) {
  const list = Array.isArray(arr) ? arr.map(String).filter(Boolean) : [];
  if (list.length <= max) return list.join(', ');
  return list.slice(0, max).join(', ') + ` (+${list.length - max} more)`;
}

export default function SystemStatusBar({ me }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');

  const refresh = React.useCallback(async () => {
    if (!me || me.role !== 'admin') return;
    try {
      const r = await fetch('/api/health/runtime-status', { credentials: 'include', cache: 'no-store' });
      const j = await r.json().catch(() => null);
      if (!j || !j.ok) throw new Error(String(j?.message || j?.error || 'status_failed'));
      setData(j);
      setErr('');
    } catch (e) {
      setErr(String(e?.message || e || 'status_failed'));
    }
  }, [me]);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!me || me.role !== 'admin') return null;

  const routes = data?.routes || null;
  const migrations = data?.migrations || null;

  const routesKind = routes
    ? (routes.ok ? 'ok' : 'bad')
    : (err ? 'bad' : 'warn');
  const migrationsKind = migrations
    ? (migrations.ok ? 'ok' : (migrations.pending_total > 0 ? 'warn' : 'bad'))
    : (err ? 'bad' : 'warn');

  const routesValue = routes
    ? (routes.ok ? 'all mounted' : `missing ${routes.missing_modules?.length || 0}`)
    : (err ? 'error' : '…');

  const migrationsValue = migrations
    ? (migrations.ok ? 'all done' : `pending ${migrations.pending_total || 0}`)
    : (err ? 'error' : '…');

  const routesTitle = routes
    ? (routes.ok
      ? `All active modules mounted (${routes.mounted_active_installed}/${routes.total_active_installed}).`
      : `Missing module mounts: ${toLineList(routes.missing_modules || [])}`)
    : (err ? `Status fetch error: ${err}` : 'Loading…');

  const migrationsTitle = migrations
    ? (migrations.ok
      ? 'All installed module migrations are applied.'
      : `Pending migrations: ${toLineList((migrations.modules_with_pending || []).map((m) => `${m.id}(${m.pending})`))}`)
    : (err ? `Status fetch error: ${err}` : 'Loading…');

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Pill label="Routes" value={routesValue} kind={routesKind} title={routesTitle} />
      <Pill label="Migrations" value={migrationsValue} kind={migrationsKind} title={migrationsTitle} />
    </div>
  );
}

